import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCurrentUser,
  selectUserDataLoading,
} from '../store/user/user.selector';
import { updateUserProfileRequest } from '../store/user/user.action';
import { IUser, IUserRole, ISignInMethod } from '../types';

interface UseUserReturn {
  user: IUser | null;
  loading: boolean;
  getUserDisplayName: () => string;
  getUserFirstName: () => string;
  getUserLastName: () => string;
  getUserInitials: () => string;
  getUserEmail: () => string;
  getUserAvatar: () => string | null;
  getUserRole: () => IUserRole;
  getUserId: () => string | null;
  isEmailVerified: () => boolean;
  isSuperAdmin: () => boolean;
  getSignInMethod: () => ISignInMethod;
  getUserSub: () => string;
  getUserCreatedAt: () => string;
  getUserUpdatedAt: () => string;
  updateUser: (newUserData: Partial<IUser>) => void;
  isLoggedIn: boolean;
}

export const useUser = (): UseUserReturn => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser) as IUser | null;
  const loading = useSelector(selectUserDataLoading) as boolean;

  // Utility functions for user data
  const getUserDisplayName = useCallback((): string => {
    if (!user) return 'User';

    // Construct full name from first_name and last_name
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || user.email || 'User';
  }, [user]);

  const getUserFirstName = useCallback((): string => {
    if (!user) return 'there';
    return user.first_name || 'there';
  }, [user]);

  const getUserLastName = useCallback((): string => {
    if (!user) return '';
    return user.last_name || '';
  }, [user]);

  const getUserInitials = useCallback((): string => {
    if (!user) return 'U';

    const firstName = user.first_name || '';
    const lastName = user.last_name || '';

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName[0].toUpperCase();
    } else if (lastName) {
      return lastName[0].toUpperCase();
    } else if (user.email) {
      return user.email[0].toUpperCase();
    }

    return 'U';
  }, [user]);

  const getUserEmail = useCallback((): string => {
    return user?.email || '';
  }, [user]);

  const getUserAvatar = useCallback((): string | null => {
    return user?.profile_picture || null;
  }, [user]);

  const getUserRole = useCallback((): IUserRole => {
    return user?.role || IUserRole.MEMBER;
  }, [user]);

  const getUserId = useCallback((): string | null => {
    return user?._id || null;
  }, [user]);

  const isEmailVerified = useCallback((): boolean => {
    return user?.email_verified || false;
  }, [user]);

  const isSuperAdmin = useCallback((): boolean => {
    return user?.is_super_admin || false;
  }, [user]);

  const getSignInMethod = useCallback((): ISignInMethod => {
    return user?.sign_in_method || ISignInMethod.EMAIL;
  }, [user]);

  const getUserSub = useCallback((): string => {
    return user?.sub || '';
  }, [user]);

  const getUserCreatedAt = useCallback((): string => {
    return user?.createdAt?.toString() || '';
  }, [user]);

  const getUserUpdatedAt = useCallback((): string => {
    return user?.updatedAt?.toString() || '';
  }, [user]);

  const updateUser = useCallback(
    (newUserData: Partial<IUser>): void => {
      dispatch(updateUserProfileRequest(newUserData));
    },
    [dispatch]
  );

  return {
    user,
    loading,
    getUserDisplayName,
    getUserFirstName,
    getUserLastName,
    getUserInitials,
    getUserEmail,
    getUserAvatar,
    getUserRole,
    getUserId,
    isEmailVerified,
    isSuperAdmin,
    getSignInMethod,
    getUserSub,
    getUserCreatedAt,
    getUserUpdatedAt,
    updateUser,
    isLoggedIn: !!user,
  };
};

export default useUser;
